// Hybrid (FK + heuristic) detection of renewed (superseded) insurance rows.
// Returns a Map keyed by insurance id with `isSuperseded` and an optional
// `successor` reference. Consumed by SummaryStrip (count adjustments), the
// row demotion + muted "Expired" badge (expired-superseded only), the new
// "Renewed" pill (expiring-superseded), and the inline RenewalReference link.
//
// A record is superseded when it has a qualifying ACTIVE successor:
//   - FK match (`successor.renewedFromId === item.id`) takes precedence; OR
//   - the heuristic fires — insurance-types overlap (or matching subjectName
//     for licence/accreditation), successor.startDate >= original.expiryDate
//     − 14 days, and successor.startDate >= original.startDate.
// Only originals whose derived status is `expired` or `expiring` are
// considered (a far-future renewal must never relabel a still-current policy);
// archived items are skipped on both sides. The successor-matching predicate
// is the cross-stack contract in tests/shared/insuranceRenewalFixtures/ and is
// mirrored by `insuranceSupersededIds` in the backend snapshot service.

import { useMemo } from "react";
import type { Insurance } from "../types";
import { deriveInsuranceStatus } from "../utils/deriveInsuranceStatus";

export interface HistoricalSuccessor {
  id: string;
  name: string;
  policyNumber?: string;
}

export interface HistoricalEntry {
  isSuperseded: boolean;
  successor?: HistoricalSuccessor;
}

interface UseInsuranceHistoricalOptions {
  today: Date;
}

const FOURTEEN_DAYS_MS = 14 * 86_400_000;

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function isHeuristicMatch(original: Insurance, candidate: Insurance): boolean {
  if (candidate.id === original.id) return false;
  if (candidate.archivedAt) return false;

  // Subject overlap: type-array overlap for insurance, subjectName equality
  // for licence/accreditation.
  if (original.category === "insurance") {
    const origTypes = original.insuranceTypes ?? [];
    const candTypes = candidate.insuranceTypes ?? [];
    const hasOverlap = candTypes.some((t) => origTypes.includes(t));
    if (!hasOverlap) return false;
  } else {
    if (!original.subjectName || !candidate.subjectName) return false;
    if (original.subjectName !== candidate.subjectName) return false;
  }

  const origExpiryMs = isoToUtcMs(original.expiryDate);
  const candStartMs = isoToUtcMs(candidate.startDate);
  const origStartMs = isoToUtcMs(original.startDate);
  // Malformed dates parse to NaN. Comparing against NaN is always false, so
  // without an explicit guard the `<` checks below would fall through to a
  // (wrong) match — reject up front so a malformed record yields no match.
  if (
    Number.isNaN(origExpiryMs) ||
    Number.isNaN(candStartMs) ||
    Number.isNaN(origStartMs)
  ) {
    return false;
  }
  if (candStartMs < origExpiryMs - FOURTEEN_DAYS_MS) return false;
  if (candStartMs < origStartMs) return false;

  return true;
}

export function useInsuranceHistorical(
  items: Insurance[],
  opts: UseInsuranceHistoricalOptions,
): Map<string, HistoricalEntry> {
  const todayIso = opts.today.toISOString().slice(0, 10);
  return useMemo(() => {
    const map = new Map<string, HistoricalEntry>();
    for (const item of items) {
      map.set(item.id, { isSuperseded: false });
    }

    // FK successor index built once (renewedFromId → active successor) so the
    // O(n²) heuristic scan only runs for originals the FK doesn't resolve —
    // mirrors the backend perf note as the register grows.
    const fkSuccessors = new Map<string, Insurance>();
    for (const s of items) {
      if (s.renewedFromId && !s.archivedAt && !fkSuccessors.has(s.renewedFromId)) {
        fkSuccessors.set(s.renewedFromId, s);
      }
    }

    for (const original of items) {
      if (original.archivedAt) continue;
      // The API doesn't populate `derivedStatus`, so derive locally with the
      // injected anchor. Only expiring/expired originals are eligible — a
      // still-current policy must not be relabelled by a far-future renewal.
      const status =
        original.derivedStatus ??
        deriveInsuranceStatus(
          original.expiryDate,
          original.startDate,
          original.reminderDays,
          todayIso,
        );
      if (status !== "expired" && status !== "expiring") continue;

      const fkMatch = fkSuccessors.get(original.id);
      const successor =
        fkMatch ?? items.find((cand) => isHeuristicMatch(original, cand));
      if (successor) {
        map.set(original.id, {
          isSuperseded: true,
          successor: {
            id: successor.id,
            name: successor.name,
            policyNumber: successor.policyNumber,
          },
        });
      }
    }

    return map;
  }, [items, todayIso]);
}
